import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api, AED, AED_short } from '../../lib/api';
import MobileShell from '../../components/MobileShell';
import {
  MapPin, Camera, Sparkles, AlertTriangle, Receipt, FileText, Users,
  ChevronRight, Building2,
} from 'lucide-react';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'snags',    label: 'Snags' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'docs',     label: 'Docs' },
];

export default function MobilePropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [snags, setSnags] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    Promise.all([
      api.get(`/properties/${id}`),
      api.get('/snags', { params: { property_id: id } }),
      api.get('/invoices', { params: { property_id: id } }).catch(() => ({ data: [] })),
      api.get('/docs', { params: { property_id: id } }).catch(() => ({ data: [] })),
    ]).then(([pr, sn, inv, dc]) => {
      setP(pr.data);
      setSnags(sn.data);
      setInvoices(inv.data);
      setDocs(dc.data);
    });
  }, [id]);

  if (!p) return <MobileShell noHeader><div className="pt-20 text-center text-slate-400">Loading…</div></MobileShell>;

  const purchase = p.purchase_price_aed || p.asking_price_aed;
  const roi = p.arv_aed && purchase
    ? (((p.arv_aed - (purchase + (p.refurb_budget_aed || 0))) / (purchase + (p.refurb_budget_aed || 0))) * 100).toFixed(0)
    : null;

  return (
    <MobileShell noHeader>
      {/* Hero */}
      <div className="-mx-4 mb-4 relative">
        {p.hero_image_url && (
          <div className="h-56 bg-slate-200"><img src={p.hero_image_url} alt="" className="w-full h-full object-cover"/></div>
        )}
        {/* Back button overlay */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white/95 shadow-md grid place-items-center"
          data-testid="m-pd-back"
        >
          <ChevronRight size={20} className="rotate-180"/>
        </button>
        {p.deal_score != null && (
          <div className="absolute top-3 right-3 w-14 h-14 rounded-full bg-white/95 grid place-items-center shadow-md">
            <div className="text-center leading-none">
              <div className="text-[8px] uppercase tracking-widest text-slate-500">Score</div>
              <div className="font-serif text-xl text-[#679286]">{p.deal_score}</div>
            </div>
          </div>
        )}
        <div className="absolute bottom-3 left-4 right-4">
          <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-white/95 text-slate-800 shadow-sm mb-2">
            {p.stage?.replace('_',' ')}
          </span>
          <h1 className="font-serif text-2xl text-white leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
            {p.title}
          </h1>
          <div className="text-xs text-white/90 flex items-center gap-1 mt-1" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            <MapPin size={11}/> {p.community}{p.building ? ` · ${p.building}` : ''}
          </div>
        </div>
      </div>

      {/* Stat bento */}
      <div className="grid grid-cols-3 gap-2 mb-5" data-testid="m-pd-stats">
        <StatMini label="Purchase" value={AED_short(purchase)}/>
        <StatMini label="Refurb" value={AED_short(p.refurb_budget_aed)}/>
        <StatMini label="ARV" value={AED_short(p.arv_aed)}/>
        <StatMini label="ROI" value={roi ? `${roi}%` : '—'} accent={roi >= 15 ? 'green' : 'amber'}/>
        <StatMini label="Beds" value={p.bedrooms ?? '—'}/>
        <StatMini label="Size" value={p.size_sqft ? `${p.size_sqft} sqft` : '—'}/>
      </div>

      {/* AI Analysis quick tile */}
      {p.ai_analysis && (
        <Link
          to={`/app/properties/${id}`}
          className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-br from-[#EEF2F0] to-[#DDE7E1] border border-white/70 mb-5"
          data-testid="m-pd-ai-cta"
        >
          <div className="w-10 h-10 rounded-full bg-white grid place-items-center flex-shrink-0"><Sparkles size={16} className="text-[#679286]"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">Iris AI valuation</div>
            <div className="text-sm text-slate-800 line-clamp-2">{p.ai_analysis.summary || 'View full AI analysis on desktop'}</div>
          </div>
          <ChevronRight size={16} className="text-slate-400"/>
        </Link>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-3 scrollbar-none sticky top-0 z-10 bg-gradient-to-b from-[#F5F1EB] via-[#F5F1EB] to-transparent">
        {TABS.map(t => {
          const badge = t.key === 'snags' ? snags.length : t.key === 'invoices' ? invoices.length : t.key === 'docs' ? docs.length : null;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`m-pd-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                tab === t.key ? 'bg-[#1A2530] text-white shadow-md' : 'bg-white/70 text-slate-700 border border-white/60'
              }`}
            >
              {t.label}{badge != null && <span className={`ml-1 ${tab === t.key ? 'opacity-80' : 'opacity-50'}`}>{badge}</span>}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
          className="pb-32"
        >
          {tab === 'overview' && <OverviewTab p={p}/>}
          {tab === 'snags' && <SnagsList snags={snags}/>}
          {tab === 'invoices' && <InvoicesList invoices={invoices}/>}
          {tab === 'docs' && <DocsList docs={docs}/>}
        </motion.div>
      </AnimatePresence>

      {/* Sticky bottom actions */}
      <div className="fixed bottom-24 left-0 right-0 z-30 px-4">
        <div className="flex gap-2">
          <Link
            to={`/app/mobile-snag?property_id=${id}`}
            data-testid="m-pd-log-snag"
            className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold shadow-xl flex items-center justify-center gap-2 active:scale-95 transition-transform"
          >
            <Camera size={16}/> Log a snag
          </Link>
          <Link
            to={`/app/properties/${id}`}
            data-testid="m-pd-full-view"
            className="px-4 py-4 rounded-2xl bg-white text-slate-800 shadow-xl border border-slate-200 font-semibold flex items-center gap-1.5 active:scale-95 transition-transform"
          >
            Full <ChevronRight size={14}/>
          </Link>
        </div>
      </div>
    </MobileShell>
  );
}

function StatMini({ label, value, accent }) {
  const clr = accent === 'green' ? 'text-emerald-700' : accent === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="p-3 rounded-2xl bg-white/85 border border-white/70">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div className={`font-serif text-lg leading-tight ${clr}`}>{value}</div>
    </div>
  );
}

function OverviewTab({ p }) {
  return (
    <div className="space-y-4">
      {p.notes && (
        <div className="p-4 rounded-2xl bg-white/70 border border-white/60">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes</div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{p.notes}</div>
        </div>
      )}
      <div className="p-4 rounded-2xl bg-white/70 border border-white/60">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Details</div>
        <dl className="text-sm divide-y divide-slate-100">
          {p.property_type && <DetailRow k="Type" v={p.property_type.replace(/_/g,' ')}/>}
          {p.floor && <DetailRow k="Floor" v={p.floor}/>}
          {p.view && <DetailRow k="View" v={p.view}/>}
          {p.condition && <DetailRow k="Condition" v={p.condition.replace(/_/g,' ')}/>}
          {p.furnished && <DetailRow k="Furnishing" v={p.furnished.replace(/_/g,' ')}/>}
          {p.plot_size_sqft && <DetailRow k="Plot size" v={`${p.plot_size_sqft} sqft`}/>}
          {p.listed_price_aed && <DetailRow k="Listed price" v={AED(p.listed_price_aed)}/>}
          {p.sold_price_aed && <DetailRow k="Sold price" v={AED(p.sold_price_aed)}/>}
        </dl>
      </div>
    </div>
  );
}

function DetailRow({ k, v }) {
  return (
    <div className="flex justify-between py-2 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-800 font-medium capitalize">{v}</span>
    </div>
  );
}

function SnagsList({ snags }) {
  if (snags.length === 0) return <div className="text-center py-10 text-sm text-slate-400">No snags logged. Tap the red button below to log one from site.</div>;
  const sevClr = { critical: 'text-red-600 bg-red-50', major: 'text-amber-700 bg-amber-50', minor: 'text-slate-500 bg-slate-50', high: 'text-red-600 bg-red-50', medium: 'text-amber-700 bg-amber-50', low: 'text-slate-500 bg-slate-50' };
  return (
    <div className="space-y-2">
      {snags.map(s => (
        <div key={s.id} className="p-4 rounded-2xl bg-white/85 border border-white/70 flex items-start gap-3" data-testid={`m-pd-snag-${s.id}`}>
          <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${sevClr[s.severity] || 'bg-slate-100 text-slate-500'}`}>
            <AlertTriangle size={14}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{s.title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.location} · <span className="capitalize">{s.status}</span></div>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${sevClr[s.severity] || 'bg-slate-100 text-slate-500'}`}>{s.severity}</span>
        </div>
      ))}
    </div>
  );
}

function InvoicesList({ invoices }) {
  if (invoices.length === 0) return <div className="text-center py-10 text-sm text-slate-400">No invoices for this property yet.</div>;
  return (
    <div className="space-y-2">
      {invoices.map(i => (
        <div key={i.id} className="p-4 rounded-2xl bg-white/85 border border-white/70 flex items-center gap-3" data-testid={`m-pd-inv-${i.id}`}>
          <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center flex-shrink-0"><Receipt size={14} className="text-slate-600"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{i.invoice_number || i.id?.slice(0,8)}</div>
            <div className="text-xs text-slate-500 truncate">{i.description}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium">{AED_short(i.amount_aed)}</div>
            <div className="text-[10px] text-slate-500 uppercase">{i.status?.replace(/_/g,' ')}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocsList({ docs }) {
  if (docs.length === 0) return <div className="text-center py-10 text-sm text-slate-400">No documents uploaded.</div>;
  return (
    <div className="space-y-2">
      {docs.map(d => (
        <div key={d.id} className="p-4 rounded-2xl bg-white/85 border border-white/70 flex items-center gap-3" data-testid={`m-pd-doc-${d.id}`}>
          <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center flex-shrink-0"><FileText size={14} className="text-slate-600"/></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{d.title || d.filename}</div>
            <div className="text-xs text-slate-500 truncate">{d.category}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
